import { Helmet, MetaTags } from '@dr.pogodin/react-helmet';

const DEFAULT_DESCRIPTION = 'Discover personalized restaurant recommendations and share your favorite spots with friends on Paire.';
const SITE_URL = import.meta.env.VITE_PWA_URL || '';
const DEFAULT_IMAGE = SITE_URL ? `${SITE_URL}/og-image.png` : '/og-image.png';
const SITE_NAME = 'Paire';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonLd = Record<string, any>;

interface SEOProps {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  /**
   * Canonical URL for this page. Defaults to `url` — pass explicitly when
   * the canonical target differs from the page's social-share URL (e.g. a
   * paginated or query-param view canonicalizing to its base route).
   */
  canonical?: string;
  /** JSON-LD structured data object, or multiple objects to emit as separate <script> tags. */
  jsonLd?: JsonLd | JsonLd[];
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  canonical = url,
  jsonLd,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  // Ensure image is an absolute URL for social media crawlers
  const absoluteImage = image?.startsWith('http') ? image : image ? `${SITE_URL}${image}` : DEFAULT_IMAGE;
  const jsonLdEntries = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      <MetaTags
        title={fullTitle}
        description={description}
        image={absoluteImage}
        url={url}
        siteName={SITE_NAME}
      />
      {(canonical || jsonLdEntries.length > 0) && (
        <Helmet>
          {canonical && <link rel="canonical" href={canonical} />}
          {jsonLdEntries.map((entry, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(entry)}
            </script>
          ))}
        </Helmet>
      )}
    </>
  );
}
